import React, { useState } from 'react';
import { ChevronUp, ChevronDown, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { TOP_TECHNIQUES } from '../mitre-data';

const SEV = {
  Critical: { color: '#ef4444', bg: 'rgba(239,68,68,0.1)',  border: 'rgba(239,68,68,0.25)',  icon: AlertTriangle },
  High:     { color: '#f97316', bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.25)', icon: AlertCircle  },
  Medium:   { color: '#eab308', bg: 'rgba(234,179,8,0.1)',  border: 'rgba(234,179,8,0.25)',  icon: AlertCircle  },
  Low:      { color: '#34d399', bg: 'rgba(52,211,153,0.1)', border: 'rgba(52,211,153,0.25)', icon: Info         },
};

const STATUS_STYLE = {
  Resolved:     { color: '#34d399', bg: 'rgba(52,211,153,0.1)'  },
  Investigating:{ color: '#f97316', bg: 'rgba(249,115,22,0.1)'  },
  Monitoring:   { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)'  },
};

function SeverityBadge({ severity }) {
  const s = SEV[severity] || SEV.Low;
  const Icon = s.icon;
  return (
    <span className="mitre-sev-badge" style={{ color: s.color, background: s.bg, borderColor: s.border }}>
      <Icon size={10} /> {severity}
    </span>
  );
}

export default function MitreTechniquesTable() {
  const [sort, setSort] = useState({ key: 'detections', dir: 'desc' });

  const sorted = [...TOP_TECHNIQUES].sort((a, b) => {
    const v = (x) => x[sort.key];
    if (v(a) < v(b)) return sort.dir === 'asc' ? -1 :  1;
    if (v(a) > v(b)) return sort.dir === 'asc' ?  1 : -1;
    return 0;
  });

  const toggle = (key) => setSort(prev =>
    prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' }
  );

  const SortIcon = ({ k }) => {
    if (sort.key !== k) return <ChevronDown size={12} style={{ opacity: 0.3 }} />;
    return sort.dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
  };

  return (
    <div className="mitre-techniques">
      <div className="mitre-techniques__header">
        <h3 className="mitre-techniques__title">Top Detected Techniques</h3>
        <p className="mitre-techniques__subtitle">MITRE ATT&amp;CK technique observations sorted by detection volume</p>
      </div>
      <div className="mitre-techniques__table-wrap">
        <table className="mitre-table">
          <thead>
            <tr>
              <th className="mitre-table__th mitre-table__th--sort" onClick={() => toggle('id')}>Technique <SortIcon k="id" /></th>
              <th className="mitre-table__th">Name</th>
              <th className="mitre-table__th mitre-table__th--sort" onClick={() => toggle('tactic')}>Tactic <SortIcon k="tactic" /></th>
              <th className="mitre-table__th mitre-table__th--sort mitre-table__th--num" onClick={() => toggle('detections')}>Detections <SortIcon k="detections" /></th>
              <th className="mitre-table__th mitre-table__th--sort" onClick={() => toggle('severity')}>Severity <SortIcon k="severity" /></th>
              <th className="mitre-table__th">Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((t) => {
              const st = STATUS_STYLE[t.status] || STATUS_STYLE.Monitoring;
              return (
                <tr key={t.id} className="mitre-table__row">
                  <td className="mitre-table__td">
                    <a className="mitre-technique-id" href={`https://attack.mitre.org/techniques/${t.id}`} target="_blank" rel="noopener noreferrer">{t.id}</a>
                  </td>
                  <td className="mitre-table__td mitre-table__td--name">{t.name}</td>
                  <td className="mitre-table__td">
                    <span className="mitre-tactic-chip">{t.tactic}</span>
                  </td>
                  <td className="mitre-table__td mitre-table__td--num">
                    <div className="mitre-det-bar">
                      <div className="mitre-det-bar__fill" style={{ width: `${Math.min(100, (t.detections / 52) * 100)}%`, background: SEV[t.severity]?.color || '#34d399' }} />
                      <span className="mitre-det-bar__num">{t.detections}</span>
                    </div>
                  </td>
                  <td className="mitre-table__td"><SeverityBadge severity={t.severity} /></td>
                  <td className="mitre-table__td">
                    <span className="mitre-status-chip" style={{ color: st.color, background: st.bg }}>{t.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
